import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { Platform } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { ConnectionStateService, Connectivity } from '../../shared/services/connection-state.service';
import { SessionController } from './session-controller';
import { SessionState } from './session-state-types';

/**
 * One banner for the states in which the app keeps running without its backend (#26615):
 * `degraded` on both platforms, `offline` on the web (the mobile app shows its own offline hints).
 *
 * A maintenance window also puts the session `offline`, but that is the maintenance snack bar's
 * story to tell — it shows the countdown — so the banner stays quiet while maintenance is on, and
 * also in the moment after it ends, while the socket is already connected and the session is on
 * its way back to `online`.
 */
@Component({
  selector: 'app-session-status',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  template: `<div class="session-status" *ngIf="messageKey$ | async as key" role="status">{{ key | translate }}</div>`,
  styles: [
    `
      .session-status {
        position: fixed;
        top: var(--ion-safe-area-top, 0px);
        left: 0;
        right: 0;
        z-index: 9000;
        padding: 6px 12px;
        text-align: center;
        font-size: 13px;
        color: #fff;
        background: #b26a00;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
      }
    `,
  ],
})
export class SessionStatusComponent {
  public readonly messageKey$: Observable<string | null>;

  constructor(session: SessionController, connectionState: ConnectionStateService, platform: Platform) {
    const hybrid = platform.is('hybrid');
    this.messageKey$ = combineLatest([session.state$, connectionState.state$]).pipe(
      map(([state, connectivity]) => messageKeyFor(state, connectivity, hybrid)),
      distinctUntilChanged()
    );
  }
}

export function messageKeyFor(state: SessionState, connectivity: Connectivity, hybrid: boolean): string | null {
  if (connectivity.maintenance) {
    return null;
  }
  if (state === 'degraded') {
    return 'session.degraded';
  }
  if (state === 'offline' && !hybrid && connectivity.socket !== 'connected') {
    return 'session.offline';
  }
  return null;
}
